//frontend/src/components/Layout/Navbar.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { Navbar as BootstrapNavbar, Container, Nav, Badge, Dropdown } from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { FaBell, FaUser, FaSignOutAlt, FaCog, FaTachometerAlt } from 'react-icons/fa';
import { IoMdChatbubbles } from 'react-icons/io';
import { toast } from 'react-toastify';
import api from '../../services/api';
import './Navbar.css';

const Navbar = ({ expanded, onToggle }) => {
    const { user, logout, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [notificationCount, setNotificationCount] = useState(0);
    const [messageCount, setMessageCount] = useState(0);

    const loadCounts = useCallback(async () => {
        try {
            // Notifications
            const notifResponse = await api.get('/notifications/');
            const notifData = notifResponse.data.notifications?.data || [];
            const unreadNotifs = notifData.filter(n => !n.is_read).length;
            setNotificationCount(unreadNotifs);

            // Messages
            const messagesResponse = await api.get('/conversations/');
            const messagesData = Array.isArray(messagesResponse.data) ? messagesResponse.data :
                                (messagesResponse.data.conversations ? messagesResponse.data.conversations : []);
            const totalUnread = messagesData.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
            setMessageCount(totalUnread);
        } catch (error) {
            console.error('Error loading counts:', error);
        }
    }, []);

    useEffect(() => {
        if (isAuthenticated) {
            loadCounts();
            const interval = setInterval(loadCounts, 30000);
            return () => clearInterval(interval);
        }
    }, [isAuthenticated, loadCounts]);

    const handleLogout = () => {
        logout();
        toast.info('👋 Déconnexion réussie');
        navigate('/login');
    };

    const isRegularUser = user?.role === 'user' || !user?.role;
    const navbarVariant = isRegularUser ? 'light' : 'dark';
    const navbarBg = isRegularUser ? 'white' : 'dark';

    const getInitials = () => {
        if (!user) return '';
        const first = user.first_name?.charAt(0) || '';
        const last = user.last_name?.charAt(0) || '';
        return `${first}${last}`.toUpperCase();
    };

    const getAvatarClass = () => {
        if (user?.role === 'admin' || user?.role === 'superadmin') {
            return 'avatar-admin';
        }
        return 'avatar-user';
    };

    const getDashboardPath = () => {
        if (user?.role === 'superadmin') return '/superadmin';
        if (user?.role === 'admin') return '/admin';
        return '/dashboard';
    };

    const getChatPath = () => {
        if (user?.role === 'superadmin') return '/superadmin/chat';
        if (user?.role === 'admin') return '/admin/chat';
        return '/chat';
    };

    const isOnDashboard = () => {
        const dashboardPath = getDashboardPath();
        return location.pathname === dashboardPath;
    };

    return (
        <BootstrapNavbar
            bg={navbarBg}
            variant={navbarVariant}
            expand="lg"
            expanded={expanded}
            onToggle={onToggle}
            className="navbar-custom"
        >
            <Container>
                <BootstrapNavbar.Brand as={Link} to="/" className="navbar-brand-custom">
                    <span className="brand-icon">🍁</span>
                    <span className="brand-text">E-Visa Canada</span>
                </BootstrapNavbar.Brand>

                <BootstrapNavbar.Toggle aria-controls="basic-navbar-nav" />

                <BootstrapNavbar.Collapse id="basic-navbar-nav">
                    <Nav className="ms-auto align-items-center">
                        {isAuthenticated ? (
                            <>
                                <Nav.Link
                                    as={Link}
                                    to="/notifications"
                                    className="nav-icon-link position-relative"
                                    title="Notifications"
                                    onClick={() => { if (expanded) onToggle(); }}
                                >
                                    <FaBell size={20} />
                                    {notificationCount > 0 && (
                                        <Badge 
                                            bg="danger" 
                                            className="notification-badge"
                                            pill
                                        >
                                            {notificationCount}
                                        </Badge>
                                    )}
                                </Nav.Link>

                                <Nav.Link
                                    as={Link}
                                    to={getChatPath()}
                                    className="nav-icon-link position-relative"
                                    title="Messages"
                                    onClick={() => { if (expanded) onToggle(); }}
                                >
                                    <IoMdChatbubbles size={22} />
                                    {messageCount > 0 && (
                                        <Badge 
                                            bg="success" 
                                            className="notification-badge"
                                            pill
                                        >
                                            {messageCount}
                                        </Badge>
                                    )}
                                </Nav.Link>

                                <Dropdown align="end" className="user-dropdown-navbar">
                                    <Dropdown.Toggle 
                                        variant="link" 
                                        id="user-dropdown"
                                        className="user-dropdown-toggle-navbar"
                                    >
                                        <div className="d-flex align-items-center">
                                            {user?.photo ? (
                                                <img 
                                                    src={user.photo} 
                                                    alt="Profile" 
                                                    className="user-avatar-navbar"
                                                />
                                            ) : (
                                                <div className={`user-avatar-placeholder-navbar ${getAvatarClass()}`}>
                                                    {getInitials()}
                                                </div>
                                            )}
                                            <span className="user-name-navbar ms-2 d-none d-lg-inline">
                                                {user?.first_name}
                                            </span>
                                        </div>
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu className="user-dropdown-menu">
                                        <div className="dropdown-user-info-section">
                                            <div className="d-flex align-items-center">
                                                {user?.photo ? (
                                                    <img 
                                                        src={user.photo} 
                                                        alt="Profile" 
                                                        className="dropdown-user-avatar"
                                                    />
                                                ) : (
                                                    <div className={`dropdown-user-avatar-placeholder ${getAvatarClass()}`}>
                                                        {getInitials()}
                                                    </div>
                                                )}
                                                <div className="ms-3">
                                                    <div className="dropdown-user-name">
                                                        {user?.first_name} {user?.last_name}
                                                    </div>
                                                    <div className="dropdown-user-role">
                                                        {user?.role === 'admin' ? 'Administrateur' : 
                                                         user?.role === 'superadmin' ? 'Super Admin' : 
                                                         'Utilisateur'}
                                                    </div>
                                                    <div className="dropdown-user-email">
                                                        {user?.email}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <Dropdown.Divider />
                                        
                                        {!isOnDashboard() && (
                                            <>
                                                <Dropdown.Item
                                                    as={Link}
                                                    to={getDashboardPath()}
                                                    className="dropdown-item-custom"
                                                    onClick={() => { if (expanded) onToggle(); }}
                                                >
                                                    <FaTachometerAlt className="me-2" />
                                                    Accéder au Dashboard
                                                </Dropdown.Item>
                                                <Dropdown.Divider />
                                            </>
                                        )}
                                        
                                        <Dropdown.Item as={Link} to="/profile" className="dropdown-item-custom" onClick={() => { if (expanded) onToggle(); }}>
                                            <FaUser className="me-2" />
                                            Mon profil
                                        </Dropdown.Item>
                                        
                                        <Dropdown.Item as={Link} to="/settings" className="dropdown-item-custom" onClick={() => { if (expanded) onToggle(); }}>
                                            <FaCog className="me-2" />
                                            Paramètres
                                        </Dropdown.Item>
                                        
                                        <Dropdown.Divider />
                                        
                                        <Dropdown.Item onClick={handleLogout} className="dropdown-item-custom text-danger">
                                            <FaSignOutAlt className="me-2" />
                                            Déconnexion
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </>
                        ) : (
                            <>
                                <Nav.Link as={Link} to="/login" className="nav-link-custom">
                                    Connexion
                                </Nav.Link>
                                <Link to="/register" className="btn-register-nav">
                                    S'inscrire
                                </Link>
                            </>
                        )}
                    </Nav>
                </BootstrapNavbar.Collapse>
            </Container>
        </BootstrapNavbar>
    );
};

export default Navbar;